/**
 * Dashboard Page
 * Main landing page after login - Redesigned with Week Calendar and Trip Overview
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useMonthlySummary } from '@/hooks/useFlights';
import { useThisWeekTrips } from '@/hooks/useWeekTrips';
import { usePilotDutyStatus } from '@/hooks/useDutyTime';
import DutyStatusCard from '@/components/DutyStatusCard';
import Button from '@/components/ui/Button';
import WeekCalendar from '@/components/calendar/WeekCalendar';
import ExpandableFlightLeg from '@/components/trip/ExpandableFlightLeg';
import {
  Card,
  CardBody,
} from '@/components/ui/Card';
import {
  CalendarDaysIcon,
  BookOpenIcon,
  ClockIcon,
  CurrencyDollarIcon,
  PaperAirplaneIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Track expanded flight legs
  const [expandedLegs, setExpandedLegs] = useState<Record<string, boolean>>({});

  // Get current date for monthly summary
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  // Fetch data
  const { data: weekTrips, isLoading: isLoadingTrips, isError: isTripsError } = useThisWeekTrips();
  const { data: dutyStatus } = usePilotDutyStatus(user?.id);
  const { data: monthlySummary, isLoading: isLoadingSummary } = useMonthlySummary(
    user?.id || 0,
    currentYear,
    currentMonth
  );

  const isPilot = user?.role === 'pilot';

  // Get today's trips
  const todayTrips = useMemo(() => {
    if (!weekTrips) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return weekTrips.filter((trip) => {
      const [sy, sm, sd] = trip.startDate.slice(0, 10).split('-').map(Number);
      const tripStart = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
      const [ey, em, ed] = trip.endDate.slice(0, 10).split('-').map(Number);
      const tripEnd = new Date(ey, em - 1, ed, 23, 59, 59, 999);

      return today >= tripStart && today <= tripEnd;
    });
  }, [weekTrips]);

  const toggleLeg = (tripId: number, legNumber: number) => {
    const key = `${tripId}-${legNumber}`;
    setExpandedLegs((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleTripClick = (tripId: number) => {
    navigate(`/trips/${tripId}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        {/* Welcome Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
            Welcome, {user?.firstName || 'Pilot'}
          </h1>
          <p className="text-muted-foreground mt-1">
            Here's your schedule for the week
          </p>
        </div>

        {/* Week Calendar */}
        <div className="mb-6">
          {isLoadingTrips ? (
            <div className="bg-card rounded-lg shadow border border-border p-8">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-3 text-muted-foreground">Loading your schedule...</span>
              </div>
            </div>
          ) : isTripsError ? (
            <div className="bg-card rounded-lg shadow border border-danger/30 p-8 text-center">
              <ExclamationTriangleIcon className="w-10 h-10 mx-auto text-danger mb-2" />
              <p className="text-foreground font-medium">Unable to load schedule</p>
              <p className="text-muted-foreground text-sm mt-1">Check your connection and refresh the page</p>
            </div>
          ) : (
            <WeekCalendar
              trips={weekTrips || []}
              onTripClick={handleTripClick}
            />
          )}
        </div>

        {/* Today's Trips */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-foreground">Today's Trips</h2>
            {todayTrips.length > 0 && (
              <span className="text-sm text-muted-foreground">
                {todayTrips.length} trip{todayTrips.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {isLoadingTrips ? (
            <div className="space-y-4">
              {[1, 2].map((i) => (
                <div key={i} className="bg-card rounded-lg shadow p-4 animate-pulse">
                  <div className="h-6 w-32 bg-muted rounded mb-3"></div>
                  <div className="h-12 bg-muted rounded"></div>
                </div>
              ))}
            </div>
          ) : todayTrips.length === 0 ? (
            <div className="bg-card rounded-lg shadow border border-border p-8 text-center">
              <PaperAirplaneIcon className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <h3 className="text-lg font-medium text-foreground mb-1">No trips today</h3>
              <p className="text-muted-foreground">
                Check the calendar above for upcoming trips
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {todayTrips.map((trip) => (
                <div
                  key={trip.id}
                  className="bg-card rounded-lg shadow border border-border overflow-hidden"
                >
                  {/* Trip Header */}
                  <div className="p-4 border-b border-border flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">
                        Trip {trip.qNumber}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {trip.tailNumber && `${trip.tailNumber}`}
                        {trip.aircraftType && ` (${trip.aircraftType})`}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate(`/trips/${trip.id}`)}
                      className="text-primary hover:text-primary/80 text-sm font-medium flex items-center gap-1"
                    >
                      View Details
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Flight Legs */}
                  <div className="p-4 space-y-3">
                    {trip.flights
                      .filter((f) => {
                        // Only show flights for today
                        const flightDate = new Date(f.date);
                        flightDate.setHours(0, 0, 0, 0);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return flightDate.getTime() === today.getTime();
                      })
                      .sort((a, b) => a.legNumber - b.legNumber)
                      .map((flight) => {
                        const key = `${trip.id}-${flight.legNumber}`;
                        return (
                          <ExpandableFlightLeg
                            key={flight.id}
                            flight={flight}
                            legNumber={flight.legNumber}
                            isExpanded={expandedLegs[key] || false}
                            onToggle={() => toggleLeg(trip.id, flight.legNumber)}
                            tripId={trip.id}
                            tripStatus={trip.status}
                          />
                        );
                      })}

                    {/* Show all flights link if more than today's */}
                    {trip.flights.length >
                      trip.flights.filter((f) => {
                        const flightDate = new Date(f.date);
                        flightDate.setHours(0, 0, 0, 0);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return flightDate.getTime() === today.getTime();
                      }).length && (
                      <p className="text-sm text-center text-muted-foreground pt-2">
                        {trip.flights.length} total flights in this trip
                      </p>
                    )}
                  </div>

                  {/* Quick Action */}
                  {(trip.status === 'PENDING_REVIEW' || trip.status === 'IN_PROGRESS') && (
                    <div className="px-4 pb-4">
                      <button
                        onClick={() => navigate(`/trips/${trip.id}/wizard`)}
                        className="w-full py-3 px-4 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-lg transition-colors"
                      >
                        Complete Flights
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Duty Time Status */}
        {dutyStatus && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-4">Duty Time</h2>
            <DutyStatusCard status={dutyStatus} compact />
          </div>
        )}

        {/* Quick Actions */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="secondary"
              size="lg"
              icon={<CalendarDaysIcon className="w-5 h-5" />}
              iconPosition="left"
              onClick={() => navigate('/trips')}
            >
              View All Trips
            </Button>
            <Button
              variant="secondary"
              size="lg"
              icon={<BookOpenIcon className="w-5 h-5" />}
              iconPosition="left"
              onClick={() => navigate('/logbook')}
            >
              My Flights
            </Button>
          </div>
        </div>

        {/* Monthly Summary - At Bottom */}
        {isPilot && (
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-4">This Month</h2>
            <div className="grid grid-cols-2 gap-4">
              {/* Hours Card */}
              <Card variant="elevated">
                <CardBody>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <ClockIcon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      {isLoadingSummary ? (
                        <div className="h-7 w-16 bg-muted rounded animate-pulse" />
                      ) : (
                        <p className="text-2xl font-bold text-foreground">
                          {monthlySummary?.totalAirTime.toFixed(1) || '0.0'}
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground">Hours Flown</p>
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* Earnings Card */}
              <Card variant="elevated">
                <CardBody>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-success/10">
                      <CurrencyDollarIcon className="w-5 h-5 text-success" />
                    </div>
                    <div>
                      {isLoadingSummary ? (
                        <div className="h-7 w-20 bg-muted rounded animate-pulse" />
                      ) : (
                        <p className="text-2xl font-bold text-success">
                          ${monthlySummary?.totalPay.toFixed(0) || '0'}
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground">Earned</p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
